import { db } from "../Utils/firebase";
import { doc, getDoc } from "firebase/firestore";
import { FIREBASE_CONVERSATIONS } from "../Utils/constant";
import { api } from "../actions/api";

/**
 * Sends a push notification via FCM by calling the backend endpoint.
 *
 * @param {Object} params
 * @param {string} params.title - Notification title
 * @param {string} params.body - Notification body content
 * @param {string} params.conversationId - The Firestore conversation ID
 * @param {boolean} params.isGroupChat - Whether it's a group chat
 * @param {Array<string>} params.recipientIds - List of user IDs to receive the notification
 */
export const sendFcmNotificationToTopic = async ({
  title,
  body,
  conversationId,
  threadId,
  isGroupChat,
  recipientIds,
  senderId,
  messageId,
}) => {
  if (!recipientIds || recipientIds.length === 0) {
    return;
  }

  try {
    // 1. Fetch the main conversation document
    const convoRef = doc(db, FIREBASE_CONVERSATIONS, conversationId);
    const convoSnap = await getDoc(convoRef);

    if (!convoSnap.exists()) {
      console.warn("Main conversation doc not found, skipping notification.");
      return;
    }

    const convoData = convoSnap.data();

    // 2. If it's a thread, fetch the thread document for specific visibility
    let threadVisibleTo = null;
    if (threadId) {
      const threadRef = doc(db, "threads", threadId);
      const threadSnap = await getDoc(threadRef);
      if (threadSnap.exists()) {
        threadVisibleTo = (threadSnap.data().visibleTo || []).map(String);
      }
    }

    // 3. Filter recipients logic
    const validRecipientIds = recipientIds.filter((id) => {
      const idStr = String(id);

      const isInParticipants = (convoData.participants || [])
        .map(String)
        .includes(idStr);
      const isVisibleInMain = (convoData.visibleTo || [])
        .map(String)
        .includes(idStr);
      const isVisibleInThread = threadVisibleTo
        ? threadVisibleTo.includes(idStr)
        : true;

      // Safety: If main is a group, also check participantDetails keys
      const isInDetails =
        convoData.type === "group"
          ? convoData.participantDetails &&
            idStr in convoData.participantDetails
          : true;

      const isNotSender = idStr !== String(senderId);

      return (
        isInParticipants &&
        isVisibleInMain &&
        isVisibleInThread &&
        isInDetails &&
        isNotSender
      );
    });

    if (validRecipientIds.length === 0) {
      return;
    }

    // 3. Strip HTML tags from body if any
    const cleanBody = body.replace(/<[^>]*>?/gm, "");

    const payload = {
      title,
      body: cleanBody,
      conversation_id: conversationId,
      is_group_chat: isGroupChat,
      recipient_ids: validRecipientIds.map((id) => String(id)),
      sender_id: String(senderId),
      message_id: messageId,
      click_action: "FLUTTER_NOTIFICATION_CLICK",
    };

    const response = await api.post("/user/chat/notification/send", payload);

    if (response.status !== 200) {
      console.warn("Failed to send FCM notification:", response.data);
    }
  } catch (error) {
    console.error("Error sending FCM notification:", error);
  }
};
